import { inject, Injectable, isDevMode, type TemplateRef } from '@angular/core';
import { type Placement } from '@floating-ui/dom';
import { ENVIRONMENT_SETTINGS } from '@reconcept-platform/env';
import { SharedViewerService } from '@reconcept-platform/shared/data-access';
import { LocalStorageService } from '@reconcept-platform/shared/util';
import { debounceTime, of, ReplaySubject } from 'rxjs';

export interface TourStepNavigate {
  tourStep: TourStep;
  currentStepNumber: number;
  totalStepsNumber: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface TourStep {
  contentTemplate: TemplateRef<any>;
  id: string;
  attachToElement: HTMLElement;
  position: Placement;
  order: number;
  sticky: boolean;
}

const STORAGE_KEY_BASE = 'info-balloon-hidden';

@Injectable({
  providedIn: 'root',
})
export class TourService {
  private viewerService = inject(SharedViewerService);
  private localStorage = inject(LocalStorageService);

  // for development purposes
  private disableStorage: boolean = isDevMode() && false;
  // for testing purposes (not storing data on User in staging and demo allows for faster retests)
  private useLocalStorageOnly = !inject(ENVIRONMENT_SETTINGS).production;

  private tourSteps: TourStep[] = [];
  private currentStep: TourStep;
  private tourSteps$: ReplaySubject<TourStep[]> = new ReplaySubject();
  private tourStepNavigate$: ReplaySubject<TourStepNavigate> = new ReplaySubject();

  getTourItemWasDismissed(id: string) {
    if (this.disableStorage) {
      return of(false);
    }

    if (this.useLocalStorageOnly) {
      return of(this.localStorage.retrieve(`${STORAGE_KEY_BASE}-${id}`));
    }

    return this.viewerService.getUiPreference(`${STORAGE_KEY_BASE}-${id}`);
  }

  storeTourItemDismissed(id: string) {
    if (this.disableStorage) {
      return of(null);
    }
    if (this.useLocalStorageOnly) {
      return of(this.localStorage.store(`${STORAGE_KEY_BASE}-${id}`, true));
    }
    return this.viewerService.storeUiPreference(`${STORAGE_KEY_BASE}-${id}`, true);
  }

  watchTourSteps() {
    return this.tourSteps$;
  }

  watchTourStepNavigate() {
    // debounce to make sure that later subscribers only get the last item
    // this is useful because multiple tour steps may be registered in a short time,
    // and then their order might change due to sorting. Once the tour-step-display subscribes
    // to the navigate, we want it to only have the latest info
    return this.tourStepNavigate$.pipe(debounceTime(0));
  }

  nextTourStep() {
    const i = this.tourSteps.indexOf(this.currentStep);
    const next = this.tourSteps[i + 1];

    if (next) {
      this.navigateToStep(next);
    }
  }

  previousTourStep() {
    const i = this.tourSteps.indexOf(this.currentStep);
    const prev = this.tourSteps[i - 1];

    if (prev) {
      this.navigateToStep(prev);
    }
  }

  registerTourStep(step: TourStep) {
    this.tourSteps.push(step);
    this.sortTourSteps();
    this.tourSteps$.next([...this.tourSteps]);
    this.navigateToStep(this.tourSteps[0]);
  }

  unregisterTourStep(stepId: string) {
    const i = this.tourSteps.findIndex((step) => step.id === stepId);
    if (i > -1) {
      this.tourSteps.splice(i, 1);
      this.tourSteps$.next([...this.tourSteps]);
    }
  }

  unregisterAll() {
    this.tourSteps = [];
    this.tourSteps$.next([...this.tourSteps]);
  }

  private navigateToStep(step: TourStep) {
    this.currentStep = step;
    const i = this.tourSteps.indexOf(step);
    const navigate: TourStepNavigate = {
      tourStep: step,
      currentStepNumber: i + 1,
      totalStepsNumber: this.tourSteps.length,
      hasPrevious: i > 0,
      hasNext: i < this.tourSteps.length - 1,
    };
    this.tourStepNavigate$.next(navigate);
  }

  private sortTourSteps() {
    this.tourSteps.sort((a, b) => {
      if (a.order < b.order) {
        return -1;
      }
      if (a.order > b.order) {
        return 1;
      }
      // a must be equal to b
      return 0;
    });
  }
}
